import { createFileRoute, Link, notFound } from "@tanstack/react-router";

import { BibleBookPage } from "../components/BibleBookPage";
import { PageChrome } from "../components/PageChrome";
import { bibleBookBySlug, biblePath } from "../lib/bible";
import {
  articleJsonLd,
  bibleOgImage,
  breadcrumbJsonLd,
  canonical,
  documentHeaders,
  faqJsonLd,
  socialMeta,
} from "../lib/seo";

/**
 * `/bible/<book>` — one book of the Bible.
 *
 * `BlogPosting` with the site's fixed publication date, and deliberately not
 * `Book`. schema.org's `Book` describes a work you could obtain — it takes an
 * ISBN, a format, a number of pages — and marking a page ABOUT Genesis as
 * though it WERE Genesis is a false claim in structured data, which is the one
 * category of SEO mistake that earns a manual action rather than a shrug.
 */
export const Route = createFileRoute("/bible/$slug")({
  loader: ({ params }) => {
    const book = bibleBookBySlug(params.slug);
    if (!book) throw notFound();
    return book;
  },
  headers: documentHeaders,
  head: ({ loaderData }) => {
    if (!loaderData) return {};
    const path = biblePath(loaderData.slug);
    const title = `The book of ${loaderData.name} — summary, author and where to start`;
    const description = `${loaderData.name} in ${loaderData.chapters} chapters: who wrote it, what it is doing, the chapters worth knowing, and what readers routinely get wrong.`;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        ...socialMeta({
          title,
          description,
          path,
          lang: "en",
          image: bibleOgImage(loaderData.slug),
          imageAlt: `The book of ${loaderData.name}`,
        }),
        {
          "script:ld+json": articleJsonLd({
            headline: `The book of ${loaderData.name}`,
            description,
            path,
            published: "2026-09-03",
          }),
        },
        {
          "script:ld+json": breadcrumbJsonLd([
            { name: "Bible Shield", path: "/" },
            { name: "Books of the Bible", path: "/bible" },
            { name: loaderData.name, path },
          ]),
        },
        ...(loaderData.faq
          ? [{ "script:ld+json": faqJsonLd(loaderData.faq) }]
          : []),
      ],
      links: [{ rel: "canonical", href: canonical(path) }],
    };
  },
  component: BibleRoute,
});

function BibleRoute() {
  const book = Route.useLoaderData();
  return (
    <PageChrome>
      <nav
        aria-label="breadcrumb"
        className="mx-auto mb-6 w-full max-w-[68ch] text-sm text-ink2"
      >
        <Link
          to="/bible"
          className="underline decoration-hair underline-offset-4 transition-colors hover:text-brand"
        >
          books of the bible
        </Link>
      </nav>
      <BibleBookPage book={book} />
    </PageChrome>
  );
}
